/**
 * The prestige offer's login prompt (AD-779 S3; prestige-cosmetic-acquisition-2026-10.md §11.2
 * item 4 (i) — the in-game channel, always on). Shown on character select, right after login:
 * each live offer to THIS account, its true accept-by date, the consequence of leaving it, and
 * one Accept control calling S2's route. Renders nothing when there is no offer, or when the
 * storefront is off (the catalogue answers 503) — a prompt is never invented.
 */

import { useEffect, useState } from 'react';

import { ApiError, acceptPrestigeOffer, getStoreCatalogue } from '../net/api.js';

import {
  ACCEPTED_HELD,
  ACCEPTED_OWNED,
  ACCEPT_LABEL,
  describeAcceptRefusal,
  offerWords,
  parsePrestige,
  type PrestigeEntry,
} from './panels/prestigeWords.js';

export function PrestigeOfferPrompt(): JSX.Element | null {
  const [offers, setOffers] = useState<PrestigeEntry[]>([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const load = (): void => {
    void getStoreCatalogue()
      .then((c) => setOffers(parsePrestige(c).filter((p) => offerWords(p) !== null)))
      .catch(() => setOffers([]));
  };
  useEffect(load, []);

  if (offers.length === 0 && !msg) return null;
  const accept = (cosmeticId: string): void => {
    setBusy(true);
    void acceptPrestigeOffer(cosmeticId)
      .then((r) => {
        setMsg(r.held ? ACCEPTED_HELD : ACCEPTED_OWNED);
        load();
      })
      .catch((e: unknown) => {
        setMsg(e instanceof ApiError ? describeAcceptRefusal(e.code) : String(e));
        // A refused offer (lapsed, gone) must not keep its Accept control on screen.
        load();
      })
      .finally(() => setBusy(false));
  };

  return (
    <div className="sub" data-testid="prestige-offer-prompt">
      {msg ? <p>{msg}</p> : null}
      {offers.map((p) => (
        <div key={p.id} data-prestige-offer={p.id}>
          <p>{offerWords(p)}</p>
          <button disabled={busy} onClick={() => accept(p.id)}>
            {ACCEPT_LABEL}
          </button>
        </div>
      ))}
    </div>
  );
}
